/**
 * Revenue Model — an embedded, interactive simulation of the revenue
 * potential of a single niche market (Discord communities running
 * economy bots like UnbelievaBoat) at scale.
 *
 * The model is the self-contained "Device Revenue Model" tool, bundled
 * as raw HTML (src/data/revenue-model.html) and rendered in an isolated
 * iframe so its own styling can't collide with the portal's Tailwind
 * theme. The HTML posts its content height to us so the iframe grows to
 * fit — no nested scrollbar. It makes no network calls and reads no
 * investor data; it's pure client-side what-if math on adjustable
 * estimates. Shown behind the portal's auth gate, with a prominent
 * "simulation, no guarantees" disclaimer above it.
 */
import { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import modelHtml from '@/data/revenue-model.html?raw';

export function RevenueModelScreen() {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(2000);

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const data = e.data as { __nurevModelHeight?: number } | null;
      if (data && typeof data === 'object' && typeof data.__nurevModelHeight === 'number') {
        const h = Math.ceil(data.__nurevModelHeight);
        if (Number.isFinite(h) && h > 400) setHeight(h + 8);
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <div className="space-y-6">
      <section>
        <h1 className="text-2xl font-bold text-nurev-text">Revenue model</h1>
        <p className="mt-1 max-w-3xl text-sm text-nurev-muted">
          A working simulation of NUREV&rsquo;s revenue from installed devices, on two channels.{' '}
          <span className="text-nurev-text-soft">Websites</span> is the original model: creators
          run their own white-label app on revshare (75% to the creator) for life during the pilot
          window, then a flat CPA per install.{' '}
          <span className="text-nurev-text-soft">Discord</span> uses the Perkio Bot Partner terms,
          with <span className="text-nurev-text-soft">UnbelievaBoat</span> as the measuring stick
          for the market (~200M members across ~2.6M servers): servers earn a 10% active share (25%
          for Pilot Partners) plus a 5% origin share for the life of each device they bring in, and
          affiliates earn 5% / 2.5% overrides on gross; NUREV keeps the rest. Switch channels at the
          top of the panel, then dial the reach, conversion, device mix, and terms. Every figure is
          an adjustable estimate, and your changes stay in your browser.
        </p>
      </section>

      <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.08] p-4">
        <Info className="mt-0.5 h-4 w-4 flex-none text-amber-300" />
        <p className="text-xs leading-relaxed text-amber-200/90">
          <span className="font-semibold text-amber-200">Simulation &mdash; no guarantees.</span>{' '}
          Every figure is a best estimate drawn from market averages and public figures, not a
          forecast, projection, or promise of returns. Actual results will differ. Nothing here is
          financial advice or a commitment of any dividend or distribution.
        </p>
      </div>

      <iframe
        ref={frameRef}
        title="NUREV revenue model simulation"
        srcDoc={modelHtml}
        className="w-full rounded-xl border border-nurev-border bg-nurev-surface"
        style={{ height, colorScheme: 'dark' }}
      />
    </div>
  );
}
